<script lang="ts">
  import { goto } from '$app/navigation';
  import { page } from '$app/stores';
  import { openPreferencesTab } from '$components/preferences/PreferencesTabs.svelte';
  import ClickableToast from '$components/toasts/ClickableToast.svelte';
  import type { BookmakerMarket, LayoutPreference, Runner } from '$types/races';
  import { isSubscribed } from '$utils/auth';
  import { prefillBookmakers } from '$utils/bookmakers';
  import { currentTime } from '$utils/time';
  import { lastBetslipPrefillAlert, shortenBookmakerName } from '$utils/ui';
  import toast from 'svelte-french-toast';
  import TablerExternalLink from '~icons/tabler/external-link';

  $: ({ race, streamed, user, hasCompanion, featureFlags } = $page.data);
  $: raceLinks = streamed?.raceData?.data?.race.links;

  export let bestPrice: BookmakerMarket | undefined;
  export let layoutPreference: LayoutPreference | undefined;
  export let runner: Runner;

  let bookmakerMarketType: 'fixedWin' | 'fixedPlace';
  $: bookmakerMarketType =
    layoutPreference?.type === 'BEST_PRICE_PLACE' ? 'fixedPlace' : 'fixedWin';

  let hovered = false;

  async function handleClick() {
    const link = streamed?.raceData?.data?.race.links.find(
      (l) => l.bookmaker === bestPrice?.bookmaker
    );
    if (link) {
      // if the user is not subscribed, open the affiliate link if it exists
      window.open(
        !$isSubscribed && link.affiliateLink
          ? link.affiliateLink
          : link.navLink,
        '_blank'
      );
    }
  }

  async function handlePlaceBet() {
    // if mobile return
    if (window.innerWidth < 768) return;
    // if (!$featureFlags?.includes('bet_prefill')) return;
    const prefillConfig = user?.data?.user.siteConfig.prefill;

    // ignore if disabled or not subscribed
    if (!$isSubscribed) {
      // show toast to subscribe
      toast(ClickableToast, {
        icon: '🔒',
        duration: 5000,
        position: 'bottom-center',
        // @ts-ignore - svelte-french-toast doesn't support custom props
        props: {
          content: 'Subscribe to enable betslip fill',
          subtitle: 'Automatically fill your betslip with your ideal stake',
          onClick: async () => {
            await goto('/subscribe/racing');
          }
        }
      });
      return;
    }

    if (!$hasCompanion) {
      // check if it has been long enough
      if ($currentTime.valueOf() - $lastBetslipPrefillAlert.valueOf() < 10000) {
        return;
      } else {
        lastBetslipPrefillAlert.set($currentTime.valueOf());
      }

      toast(ClickableToast, {
        icon: '🤖',
        duration: 5000,
        position: 'bottom-center',
        // @ts-ignore - svelte-french-toast doesn't support custom props
        props: {
          content: 'Click to Enable Betslip Prefill',
          subtitle: 'Automatically fill your betslip with your ideal stake',
          onClick: () => {
            openPreferencesTab('Betting');
          }
        }
      });
      return;
    }

    if (!prefillConfig?.quickFill) {
      // check if it has been long enough
      if (
        $currentTime.valueOf() - $lastBetslipPrefillAlert.valueOf() <
        1000000
      ) {
        return;
      } else {
        lastBetslipPrefillAlert.set($currentTime.valueOf());
      }

      toast(ClickableToast, {
        icon: '💡',
        duration: 5000,
        position: 'bottom-center',
        // @ts-ignore - svelte-french-toast doesn't support custom props
        props: {
          content: 'Quick Fill is disabled',
          subtitle: 'Click to open Preferences and enable it',
          onClick: () => {
            openPreferencesTab('Betting');
          }
        }
      });
      return;
    }

    if (
      bestPrice?.bookmaker &&
      !prefillBookmakers.includes(bestPrice.bookmaker)
    ) {
      toast(ClickableToast, {
        icon: '🙁',
        duration: 5000,
        position: 'bottom-center',
        // @ts-ignore - svelte-french-toast doesn't support custom props
        props: {
          content: 'Quick Fill is not available for this bookmaker',
          onClick: () => {}
        }
      });
      return;
    }
    // craft a link to the bookmaker's website
    // find the bookmaker navlink
    const navLink = raceLinks?.find((link) => {
      return link.bookmaker === bestPrice?.bookmaker;
    });
    if (!navLink?.navLink || !bestPrice) return;
    const url = new URL(navLink.navLink);
    // add query params (bwSelection)
    url.searchParams.append('bwSelection', runner.number.toString());
    let raceTypeConfig = prefillConfig?.defaults?.thoroughbred;
    if ($race?.meeting.type === 'Harness') {
      raceTypeConfig = prefillConfig?.defaults?.harness;
    } else if ($race?.meeting.type === 'Greyhound') {
      raceTypeConfig = prefillConfig?.defaults?.greyhound;
    }
    let stake = raceTypeConfig?.value ?? 0;
    if (raceTypeConfig?.type === 'TO_WIN') {
      const toWin = raceTypeConfig?.value ?? 0;
      stake = toWin / (bestPrice[bookmakerMarketType].price - 1);
    }

    // round down to nearest whole number
    stake = Math.floor(stake);
    if (stake === 0 && raceTypeConfig.value) {
      stake = 1;
    }
    url.searchParams.append('bwStake', stake.toString());

    if (bookmakerMarketType === 'fixedPlace') {
      url.searchParams.append('bwBetType', 'plc');
    }

    // open the link in a new tab
    window.open(url.toString(), '_blank');
  }
</script>

{#if bestPrice}
  <td
    on:click={handlePlaceBet}
    on:keypress={handlePlaceBet}
    class="table-cell cursor-pointer select-none whitespace-nowrap border-l px-2 text-center text-[13px] text-gray-900 transition-all duration-200 ease-in-out hover:bg-slate-100 hover:shadow-inner"
  >
    {bestPrice?.[bookmakerMarketType].price ?? '-'}
  </td>

  <td
    on:click={() => handleClick()}
    on:keypress={() => handleClick()}
    on:mouseenter={() => (hovered = true)}
    on:mouseleave={() => (hovered = false)}
    colspan="3"
    class="relative table-cell min-w-[100px] max-w-[100px] cursor-pointer select-none overflow-ellipsis whitespace-nowrap border border-r text-center text-[13px]
    bookmaker-{(bestPrice.bookmaker ?? '').toLowerCase().replace(' ', '')}"
  >
    {#if hovered}
      <div class="absolute bottom-0 right-0 opacity-40">
        <TablerExternalLink />
      </div>
    {/if}
    {shortenBookmakerName(bestPrice.bookmaker ?? '')}
  </td>
{/if}
